import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'bluetide-output-panel-width';
const MIN_WIDTH = 280;
const NARROW_RATIO = 0.2;
const WIDE_RATIO = 0.5;
const MAX_RATIO = 0.55;
const SNAP_THRESHOLD = 18;

export function getOutputPanelLayoutWidths(sectionWidth) {
  const total = sectionWidth || 1200;
  const narrow = Math.max(MIN_WIDTH, Math.round(total * NARROW_RATIO));
  const wide = Math.round(total * WIDE_RATIO);
  const max = Math.round(total * MAX_RATIO);
  return { narrow, wide, max };
}

function readStoredWidth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function snapWidth(width, { narrow, wide, max }) {
  const clamped = Math.min(max, Math.max(narrow, width));
  if (Math.abs(clamped - narrow) <= SNAP_THRESHOLD) return narrow;
  if (Math.abs(clamped - wide) <= SNAP_THRESHOLD) return wide;
  return clamped;
}

/**
 * Resizable width for the right-hand output / chat column.
 * Drag the panel's left edge left to widen, right to narrow.
 */
export function useOutputPanelWidth(sectionRef) {
  const [width, setWidth] = useState(MIN_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef(null);
  const hasInitializedRef = useRef(false);

  const measure = useCallback(() => {
    const sectionWidth = sectionRef.current?.getBoundingClientRect().width;
    return getOutputPanelLayoutWidths(sectionWidth);
  }, [sectionRef]);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    const layout = measure();
    const stored = readStoredWidth();
    const initial = stored != null ? snapWidth(stored, layout) : layout.narrow;
    setWidth(initial);
    hasInitializedRef.current = true;
  }, [measure]);

  const isCollapsed = useCallback(() => {
    const { narrow } = measure();
    return width <= narrow + SNAP_THRESHOLD;
  }, [measure, width]);

  const collapsed = isCollapsed();

  const clampWidth = useCallback((next) => {
    const { narrow, max } = measure();
    return Math.min(max, Math.max(narrow, next));
  }, [measure]);

  const persistWidth = useCallback((value) => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Math.round(value)));
    } catch {
      // ignore quota / private mode
    }
  }, []);

  useEffect(() => {
    if (!hasInitializedRef.current || isResizing) return;
    persistWidth(width);
  }, [width, isResizing, persistWidth]);

  useEffect(() => {
    const onWindowResize = () => {
      setWidth((current) => snapWidth(clampWidth(current), measure()));
    };
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, [clampWidth, measure]);

  const toggleCollapse = useCallback(() => {
    const layout = measure();
    if (collapsed) {
      setWidth(layout.wide);
    } else {
      setWidth(layout.narrow);
    }
  }, [collapsed, measure]);

  const onResizeStart = useCallback((event) => {
    if (event.button !== 0) return;
    event.preventDefault();

    dragRef.current = { startX: event.clientX, startWidth: width };
    setIsResizing(true);

    const onMove = (moveEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const next = clampWidth(drag.startWidth + (drag.startX - moveEvent.clientX));
      setWidth(next);
    };

    const onUp = () => {
      dragRef.current = null;
      setIsResizing(false);
      setWidth((current) => {
        const snapped = snapWidth(clampWidth(current), measure());
        persistWidth(snapped);
        return snapped;
      });
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [width, clampWidth, measure, persistWidth]);

  return {
    width,
    isResizing,
    isCollapsed: collapsed,
    toggleCollapse,
    onResizeStart
  };
}
